/** The status vocabulary — every label and colour a state gets in the console,
 *  in one table (plan 1-3). Screens never spell a state out themselves: they
 *  call `statusOf(domain, key)` and render a StatusPill. */

export type Tone =
  | 'neutral'
  | 'review'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'deadline'
  | 'web'

export interface StatusDef {
  label: string
  tone: Tone
  /** The dot blinks: something is under way right now. */
  pulse?: boolean
  /** Shown faded: the item is out of play. */
  dim?: boolean
}

/** The rank tracker's state (GET /api/tracking/status). `never` is a tracker
 *  that has not run since the process started; `idle` is one waiting for its
 *  next tick. */
const TRACKING: Record<string, StatusDef> = {
  never: { label: '실행 전', tone: 'neutral' },
  idle: { label: '대기', tone: 'neutral' },
  running: { label: '실행 중', tone: 'publishing', pulse: true },
  ok: { label: '정상', tone: 'published' },
  error: { label: '문제', tone: 'failed' },
}

/** A rank's direction. Movers and competition rows say up/down/same/new/lost/
 *  out; change events say rank_up/rank_down/rank_new/rank_lost/
 *  popularity_shift. The direction is read from this key alone, never from the
 *  sign of `delta` (plan 1-3: the sign means different things per type). */
const MOVE: Record<string, StatusDef> = {
  up: { label: '상승', tone: 'published' },
  rank_up: { label: '상승', tone: 'published' },
  down: { label: '하락', tone: 'failed' },
  rank_down: { label: '하락', tone: 'failed' },
  same: { label: '유지', tone: 'neutral' },
  new: { label: '신규', tone: 'scheduled' },
  rank_new: { label: '신규', tone: 'scheduled' },
  lost: { label: '이탈', tone: 'deadline' },
  rank_lost: { label: '이탈', tone: 'deadline' },
  out: { label: '순위 밖', tone: 'neutral', dim: true },
  popularity_shift: { label: '인기도 변화', tone: 'web' },
  metadata: { label: '문구 변경', tone: 'scheduled' },
}

/** The server's opportunity tier (internal/suggest.Opportunity). */
const OPPORTUNITY: Record<string, StatusDef> = {
  golden: { label: '최우선', tone: 'publishing' },
  hidden_gem: { label: '틈새', tone: 'web' },
  competitive: { label: '치열', tone: 'deadline' },
  low_value: { label: '낮음', tone: 'neutral' },
  unscored: { label: '미측정', tone: 'neutral', dim: true },
}

/** A 0–10 score's band; `scoreBand` picks the key. */
const SCORE: Record<string, StatusDef> = {
  good: { label: '좋음', tone: 'published' },
  fair: { label: '보통', tone: 'scheduled' },
  low: { label: '낮음', tone: 'publishing' },
  poor: { label: '나쁨', tone: 'failed' },
}

/** A 0–100 difficulty's band; `difficultyBand` picks the key. */
const DIFFICULTY: Record<string, StatusDef> = {
  easy: { label: '쉬움', tone: 'published' },
  medium: { label: '보통', tone: 'publishing' },
  hard: { label: '어려움', tone: 'failed' },
}

const APP: Record<string, StatusDef> = {
  mine: { label: '내 앱', tone: 'scheduled' },
  competitor: { label: '경쟁사', tone: 'neutral' },
}

/** Where a tracked keyword came from. */
const KEYWORD_SOURCE: Record<string, StatusDef> = {
  user: { label: '직접', tone: 'neutral' },
  suggestion: { label: '제안', tone: 'neutral' },
  asc: { label: 'ASC', tone: 'neutral' },
  listing: { label: '이름·부제', tone: 'neutral' },
}

/** A Play Console slot: an empty app shell made ahead of time. */
const SLOT: Record<string, StatusDef> = {
  created: { label: '미사용', tone: 'neutral' },
  linked: { label: '연결됨', tone: 'scheduled' },
  submitted: { label: '제출됨', tone: 'review' },
  live: { label: '출시됨', tone: 'published' },
  abandoned: { label: '폐기', tone: 'neutral', dim: true },
}

const ATTEMPT_KIND: Record<string, StatusDef> = {
  create: { label: '만들기', tone: 'neutral' },
  dry_run: { label: '시험 실행', tone: 'neutral' },
  verify: { label: '확인', tone: 'neutral' },
}

const ATTEMPT_RESULT: Record<string, StatusDef> = {
  ok: { label: '성공', tone: 'published' },
  unverified: { label: '미확인', tone: 'publishing' },
  limit: { label: '한도', tone: 'failed' },
  login_required: { label: '로그인 필요', tone: 'failed' },
  error: { label: '오류', tone: 'failed' },
}

/** A job_runs row. */
const JOB: Record<string, StatusDef> = {
  running: { label: '실행 중', tone: 'publishing', pulse: true },
  ok: { label: '성공', tone: 'published' },
  error: { label: '실패', tone: 'failed' },
  skipped: { label: '건너뜀', tone: 'neutral' },
  cancelled: { label: '취소', tone: 'neutral', dim: true },
}

/** The first-party analytics collector pull. */
const COLLECTOR: Record<string, StatusDef> = {
  unconfigured: { label: '미설정', tone: 'neutral' },
  running: { label: '수집 중', tone: 'publishing', pulse: true },
  ok: { label: '정상', tone: 'published' },
  error: { label: '실패', tone: 'failed' },
}

/** Analytics data quality: `warn` when 20% or more screen enters have no exit. */
const QUALITY: Record<string, StatusDef> = {
  ok: { label: '정상', tone: 'published' },
  warn: { label: '경고', tone: 'publishing' },
}

export const STATUS = {
  tracking: TRACKING,
  move: MOVE,
  opportunity: OPPORTUNITY,
  score: SCORE,
  difficulty: DIFFICULTY,
  app: APP,
  keywordSource: KEYWORD_SOURCE,
  slot: SLOT,
  attemptKind: ATTEMPT_KIND,
  attemptResult: ATTEMPT_RESULT,
  job: JOB,
  collector: COLLECTOR,
  quality: QUALITY,
} as const

export type StatusDomain = keyof typeof STATUS

/** The label and tone for a state. An unknown key comes back as itself,
 *  neutral, so a new server state shows up instead of vanishing. */
export function statusOf(domain: StatusDomain, key: string): StatusDef {
  return (STATUS[domain] as Record<string, StatusDef>)[key] ?? { label: key, tone: 'neutral' }
}

/** A 0–10 score's band: 7 and up good, 5 fair, 3 low, below 3 poor. */
export function scoreBand(score: number): 'good' | 'fair' | 'low' | 'poor' {
  if (score >= 7) return 'good'
  if (score >= 5) return 'fair'
  if (score >= 3) return 'low'
  return 'poor'
}

/** A 0–100 difficulty's band: under 40 easy, under 70 medium, else hard. */
export function difficultyBand(difficulty: number): 'easy' | 'medium' | 'hard' {
  if (difficulty < 40) return 'easy'
  if (difficulty < 70) return 'medium'
  return 'hard'
}

/** The tracker's state key from its status: running beats everything, a
 *  tracker with no run since boot is `never`. */
export function trackingKey(s: { running: boolean; last_at: string; last_ok: boolean } | null | undefined): string {
  if (!s) return 'never'
  if (s.running) return 'running'
  if (!s.last_at || s.last_at.startsWith('0001-')) return 'never'
  return s.last_ok ? 'ok' : 'error'
}

/** The names the job keys get on screen. A key not listed shows as itself. */
export const JOB_LABEL: Record<string, string> = {
  tracking_snapshot: '순위 스냅샷',
  asc_keyword_sync: 'ASC 키워드 동기화',
  store_rank_snapshot: '스토어 순위',
  apple_popularity: 'Apple 인기도',
  snapshot_maintenance: '스냅샷 정리',
  analytics_pull: '자체 분석 수집',
  analytics_purge: '원본 이벤트 정리',
  session_purge: '세션 정리',
  playslots_run: 'Play 슬롯 실행',
  grossing_charts: '매출 순위 수집',
}

export function jobLabel(job: string): string {
  return JOB_LABEL[job] ?? job
}

/** Class names per tone. Kept literal so Tailwind sees every one. */
export const TONE_CLASS: Record<Tone, { text: string; soft: string; dot: string; border: string }> = {
  neutral: {
    text: 'text-status-neutral',
    soft: 'bg-status-neutral-soft',
    dot: 'bg-status-neutral',
    border: 'border-status-neutral/30',
  },
  review: {
    text: 'text-status-review',
    soft: 'bg-status-review-soft',
    dot: 'bg-status-review',
    border: 'border-status-review/30',
  },
  scheduled: {
    text: 'text-status-scheduled',
    soft: 'bg-status-scheduled-soft',
    dot: 'bg-status-scheduled',
    border: 'border-status-scheduled/30',
  },
  publishing: {
    text: 'text-status-publishing',
    soft: 'bg-status-publishing-soft',
    dot: 'bg-status-publishing',
    border: 'border-status-publishing/30',
  },
  published: {
    text: 'text-status-published',
    soft: 'bg-status-published-soft',
    dot: 'bg-status-published',
    border: 'border-status-published/30',
  },
  failed: {
    text: 'text-status-failed',
    soft: 'bg-status-failed-soft',
    dot: 'bg-status-failed',
    border: 'border-status-failed/30',
  },
  deadline: {
    text: 'text-status-deadline',
    soft: 'bg-status-deadline-soft',
    dot: 'bg-status-deadline',
    border: 'border-status-deadline/30',
  },
  web: {
    text: 'text-status-web',
    soft: 'bg-status-web-soft',
    dot: 'bg-status-web',
    border: 'border-status-web/30',
  },
}
